import { useTheme } from 'styled-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { CreditCard, Information, Percentaje, Price } from '@agendapro/emerald-icons';
import { useReservation } from '@/context/ReservationsContext';
import { ServiceCollapse } from './ServiceCollapse';
import { Paragraph } from '@/UI';
import { ServiceReservationProps } from '@/context/ReservationsContext/ReservationContext.types';
import useFormatCurrency from '@/hooks/useFormatCurrency';
import usePageContext from '@/hooks/usePageContext';
import * as St from './ReservationsSummary.styles';

export const ReservationsSummary: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSalfa, companyOverview } = usePageContext();
  const { reservations } = useReservation();
  const { servicesToReserve, servicesWithProviders, bookingMode } = reservations;
  const fc = useFormatCurrency();
  const companyAllowsOnlinePayment =
    companyOverview?.allowsOnlinePayment &&
    companyOverview?.onlinePaymentCapable &&
    companyOverview?.providerOnlinePayment;

  const mustBePaidOnline = servicesToReserve.some((service) => service.mustBePaidOnline);
  const isNotOnlinePayable = servicesToReserve.filter((service) => !service.onlinePayable).length;
  const isOnlinePayable = servicesToReserve.some((service) => service.onlinePayable);
  const hasOnlineDownpayment = servicesToReserve.some(
    (service) => service.mustBePaidOnline && service.onlineDownPayment,
  );
  const onlineDiscount = servicesToReserve
    .map((service) => {
      if ((service.mustBePaidOnline || service.onlinePayable) && service.discount && !service.charlyDiscount) {
        return +service.price - +service.payingPrice;
      }
      return 0;
    })
    .reduce((a, b) => +a + +b, 0);

  const isBundle = servicesToReserve.some((service) => service.bundled);
  const allServicesFree = servicesToReserve.every((service) => service.price === '' && !!service.showPrice);

  const getTotal = (services: ServiceReservationProps[]) => {
    if (services[0].hasSession) {
      if (services[0].showPrice) {
        return { total: fc(services[0].price), online: fc(services[0].payingPrice) };
      }
      return {
        total: t('RESERVATION_SUMMARY.TO_DETERMINE'),
        online: '',
      };
    }
    const { total, online } = services
      .map((service) => {
        const isPromoActive = dayjs(service.daySelected).isBetween(
          service.charlyPromotionStartDate,
          service.charlyPromotionEndDate,
          'day',
          '[]',
        );

        if (!!service.charlyDiscount && isPromoActive) {
          return {
            total: service.payingPriceCharly || 0,
            online: service.mustBePaidOnline || service.onlinePayable ? service.payingPriceCharly || 0 : 0,
          };
        }
        let onlineTotal = 0;

        if (service.mustBePaidOnline || service.onlinePayable) {
          if (service.onlineDownPayment) {
            onlineTotal = service.onlineDownPaymentAmount || 0;
          } else {
            onlineTotal = +service.payingPrice || 0;
          }
        }
        return { total: +service.price, online: onlineTotal };
      })
      .reduce(
        (acc, curr) => ({
          total: acc.total + curr.total,
          online: acc.online + curr.online,
        }),
        { total: 0, online: 0 },
      );

    if (servicesToReserve.some((service) => !service.showPrice)) {
      return {
        total: t('RESERVATION_SUMMARY.TO_DETERMINE'),
        online: fc(online),
      };
    }
    return {
      total: fc(total),
      online: fc(online),
    };
  };

  const { total, online } = getTotal(servicesToReserve);

  const getConsecutiveServices = (services: ServiceReservationProps[], isBundle: boolean): any => {
    if (isBundle) {
      return [
        {
          serviceProvider: { id: 0, publicName: t('FIRST_PROVIDER_AVAILABLE') },
          name: services.map((service) => service.name).join(', '),
          price: services
            .map((service) => (typeof service.price === 'string' ? 0 : service.price))
            .reduce((a, b) => a + b, 0),
          payingPrice: services
            .map((service) => (typeof service.payingPrice === 'string' ? 0 : service.payingPrice))
            .reduce((a, b) => a + b, 0),
          duration: services.map((service) => service.duration).reduce((a, b) => a + b, 0),
          dateFormatted: services[0].dateFormatted,
          hourSelected: services[0].hourSelected,
          hourEnd: services[0].hourEnd,
          mustBePaidOnline,
          isVideo: services.some((service) => service.isVideo),
          hasSession: services.some((service) => service.hasSession),
          id: services[0].id,
        },
      ];
    }
    return services.map((service) => ({
      ...service,
      serviceProvider: servicesWithProviders[service.internalId || ''],
      dateFormatted: services[0].dateFormatted,
      hourSelected: services[0].hourSelected,
      hourEnd: services[0].hourEnd,
    }));
  };

  return (
    <St.Wrapper>
      <St.ReservationSummaryContainer>
        <St.TitleWrapper>
          <Paragraph size="subHeadline" className="title" color={theme.palette.grays100} align="center">{`${t(
            'RESERVATION_ALMOST_READY',
          )}`}</Paragraph>
        </St.TitleWrapper>
        {!isSalfa && (
          <>
            {(!!total || !!online || allServicesFree) && (
              <St.TotalWrapper>
                {(total || allServicesFree) && (
                  <St.Row>
                    <div className="text">
                      <Price size={25} />
                      <Paragraph weight="light" color={theme.palette.grays80}>
                        {t('TOTAL')}
                      </Paragraph>
                    </div>
                    <Paragraph weight="light" color={theme.palette.grays80}>
                      {allServicesFree ? '$0' : total}
                    </Paragraph>
                  </St.Row>
                )}
                {companyAllowsOnlinePayment && isOnlinePayable && !!online && (
                  <St.Row>
                    <div className="text">
                      <CreditCard size={25} />
                      <Paragraph
                        weight="light"
                        color={theme.palette.grays80}
                        data-testid="reservations_summary_total_online"
                      >
                        {t('RESERVATION_SUMMARY.TOTAL_ONLINE')}
                      </Paragraph>
                    </div>
                    <Paragraph weight="bold" color={theme.palette.grays80}>
                      {online}
                    </Paragraph>
                  </St.Row>
                )}
                {companyAllowsOnlinePayment && onlineDiscount > 0 && !isBundle && (
                  <St.DisclaimerWrapper isDiscount>
                    <Percentaje size={16} />
                    <Paragraph weight="light" color={theme.palette.grays80}>{`${t(
                      'RESERVATION_SUMMARY.DISCOUNT_SAVING',
                      {
                        amount: fc(onlineDiscount),
                      },
                    )}`}</Paragraph>
                  </St.DisclaimerWrapper>
                )}
              </St.TotalWrapper>
            )}
            {companyAllowsOnlinePayment &&
              isNotOnlinePayable > 0 &&
              isNotOnlinePayable !== servicesToReserve.length &&
              !isBundle && (
                <St.DisclaimerWrapper>
                  <Information size={40} />
                  <Paragraph
                    weight="light"
                    color={theme.palette.orange500}
                    className="text"
                    data-testid="reservations_summary_must_pay_later"
                  >{`${t('RESERVATION_SUMMARY.REMINDER_MUST_PAY_LATER')}`}</Paragraph>
                </St.DisclaimerWrapper>
              )}
            {companyAllowsOnlinePayment && hasOnlineDownpayment && !isBundle && (
              <St.DisclaimerWrapper>
                <Information size={40} />
                <Paragraph
                  weight="light"
                  color={theme.palette.orange500}
                  className="text"
                  data-testid="reservations_summary_must_pay"
                >{`${t('RESERVATION_SUMMARY.REMINDER_MUST_PAY')}`}</Paragraph>
              </St.DisclaimerWrapper>
            )}
          </>
        )}
        <ServiceCollapse
          services={
            isBundle || bookingMode === 'consecutive'
              ? getConsecutiveServices(servicesToReserve, isBundle)
              : servicesToReserve
          }
        />
      </St.ReservationSummaryContainer>
    </St.Wrapper>
  );
};
